// This file is large, and mostly on purpose: nearly all of it is the two GraphQL subscription
// documents below, which are declarative data rather than logic (the actual logic is the ~70-line
// hook at the bottom). It also resists the obvious split — Relay requires an operation's name to be
// prefixed by its module name, and both `IssueViewerSubscriptionMetadataSubscription` and
// `IssueViewerSubscriptionTimelineSubscription` are prefixed by `IssueViewerSubscription`. Moving
// either document to its own module means renaming a live subscription operation, which changes what
// is sent to the server. It will keep growing as the M3.4 fragment cleanup (github/issues#22622)
// inlines the remaining fragment spreads here; that is expected.
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQueryClient} from '@github-ui/react-query'
import {cacheForceRefresh} from '@github-ui/relay-query-cache/query-helpers'
import {useMemo, useState} from 'react'
import {graphql, useRelayEnvironment, useSubscription} from 'react-relay'

import type {
  IssueViewerSubscriptionMetadataSubscription,
  IssueViewerSubscriptionMetadataSubscription$data,
} from './__generated__/IssueViewerSubscriptionMetadataSubscription.graphql'
import type {
  IssueViewerSubscriptionTimelineSubscription,
  IssueViewerSubscriptionTimelineSubscription$data,
} from './__generated__/IssueViewerSubscriptionTimelineSubscription.graphql'
import {IssueViewerViewGraphqlQuery} from './IssueViewer'
import {invalidateForIssueUpdate, invalidateScopes} from './secondary-view-invalidation'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const metadataSubscription = graphql`
  subscription IssueViewerSubscriptionMetadataSubscription($issueId: ID!) {
    issueUpdated(id: $issueId) {
      issue {
        number
        repository {
          name
          owner {
            login
          }
        }
      }
      deletedCommentId @deleteRecord
      issueMetadataUpdated {
        labels(first: 100, orderBy: {field: NAME, direction: ASC}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              color
              name
              nameHTML
              description
              url
              archivedAt
            }
          }
        }
        milestone {
          id
          title
          closed
          dueOn
          progressPercentage
          url
          closedAt
        }
        # ProjectsSectionFragment was inlined here when it migrated off Relay.
        projectItems(first: 25, allowSameEnterprise: true) @connection(key: "ProjectSection_projectItems") {
          edges {
            node {
              id
              isArchived
              fieldValueByName(name: "Status") {
                ... on ProjectV2ItemFieldSingleSelectValue {
                  id
                  optionId
                  name
                  nameHTML
                  color
                }
              }
              project {
                id
                title
                closed
                number
                url
                template
                viewerCanUpdate
                hasReachedItemsLimit
                field(name: "Status") {
                  ... on ProjectV2SingleSelectField {
                    databaseId
                    id
                    name
                    options {
                      id
                      optionId
                      name
                      nameHTML
                      color
                      descriptionHTML
                      # The status field has a non unique id. We leverage the getDataID method in the Relay environment,
                      # and in this instance use the id, name, description and color to generate a unique ID:
                      # - https://github.com/github/github/pull/305841/files#diff-7187f5ed804646bc353b5176a059d44bcd6a4cd5d5f19976aebb885d1e6997b8R155
                      description
                    }
                  }
                }
              }
            }
          }
        }
        viewerCanUpdateMetadata
        # AssigneesSectionAssignees was inlined here when it migrated off Relay.
        # DevelopmentSectionFragment was inlined here when DevelopmentSection migrated off Relay.
        # MilestonesSectionMilestone was inlined here when MilestonesSectionMilestone migrated off Relay.
        # LinkedPullRequests was inlined here when it migrated off Relay.
        linkedPullRequests: closedByPullRequestsReferences(first: 10, includeClosedPrs: false, orderByState: true) {
          nodes {
            repository {
              name
              nameWithOwner
              owner {
                login
              }
            }
            url
            number
            state
            isDraft
          }
        }
        assignedActors(first: 20) {
          nodes {
            ... on Actor {
              __typename
              id
              login
              name
              profileResourcePath
              avatarUrl(size: 64)
              ... on Bot {
                isCopilot
                isAgent
                displayName
              }
            }
          }
        }
        agentAssignments {
          assignee {
            id
            login
            avatarUrl(size: 64)
            isCopilot
            isAgent
            displayName
          }
          assigner {
            __typename
            ... on Node {
              id
            }
            ... on Actor {
              login
            }
          }
        }
        repository {
          id
          databaseId
          name
          owner {
            login
          }
        }
        id
        title
        number
        databaseId
        linkedBranches(first: 25) {
          nodes {
            id
            ref {
              name
              id
              __typename
              target {
                oid
                id
              }
              repository {
                id
                nameWithOwner
                defaultBranchRef {
                  name
                  id
                  target {
                    oid
                    id
                  }
                  associatedPullRequests {
                    totalCount
                  }
                }
              }
              associatedPullRequests {
                totalCount
              }
            }
          }
        }
        closedByPullRequestsReferences(first: 25, includeClosedPrs: true) {
          nodes {
            ... on PullRequest {
              id
              url
              number
              title
              state
              isDraft
              isInMergeQueue
              createdAt
              databaseId
              repository {
                id
                name
                nameWithOwner
                owner {
                  id
                  login
                  __typename
                }
              }
              __typename
            }
          }
        }
        descriptionClosingPullRequests: closedByPullRequestsReferences(
          first: 25
          includeClosedPrs: true
          excludeUserLinked: true
        ) {
          nodes {
            id
          }
        }
        linkedPullRequestReleases(first: 10) {
          nodes {
            pullRequest {
              id
            }
            release {
              id
              tagName
              name
              url
              isPrerelease
              isLatest
            }
          }
        }
        viewerCanLinkBranches
      }
      issueBodyUpdated {
        body
        bodyHTML(unfurlReferences: true)
        bodyVersion
      }
      issueTitleUpdated {
        title
        titleHTML
        number
        url
        viewerCanUpdateNext
        id
        repository {
          name
          nameWithOwner
          owner {
            login
          }
          isArchived
          isPrivate
          isWritable
          id
        }
        issueType {
          name
          color
        }
        subIssuesSummary {
          total
          completed
        }
        state
        stateReason
        duplicateOf {
          number
          url
          repository {
            name
            owner {
              login
            }
          }
        }
        linkedPullRequests: closedByPullRequestsReferences(first: 10, includeClosedPrs: false, orderByState: true) {
          nodes {
            repository {
              name
              nameWithOwner
              owner {
                login
              }
            }
            url
            number
            state
            isDraft
          }
        }
        agentAssignments {
          __typename
        }
        closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
          nodes {
            id
            number
            url
            state
            databaseId
          }
        }
        labels(first: 100, orderBy: {field: NAME, direction: ASC}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              color
              name
              nameHTML
              description
              url
              archivedAt
            }
          }
        }
        milestone {
          id
          title
          closed
          dueOn
          progressPercentage
          url
          closedAt
        }
        assignedActors(first: 20) {
          nodes {
            ... on Actor {
              __typename
              id
              login
              name
              profileResourcePath
              avatarUrl(size: 64)
              ... on Bot {
                isCopilot
                isAgent
                displayName
              }
            }
          }
        }
      }
      issueStateUpdated {
        id
        state
        stateReason
        repository {
          id
          nameWithOwner
          owner {
            login
          }
        }
        duplicateOf {
          number
          url
          repository {
            name
            owner {
              login
            }
          }
        }
      }
      issueTypeUpdated {
        repository {
          nameWithOwner
        }
        issueType {
          name
          color
          id
          isEnabled
          description
          pinnedFields {
            __typename
            ... on IssueFieldText {
              id
              name
              dataType
            }
            ... on IssueFieldSingleSelect {
              id
              name
              dataType
            }
            ... on IssueFieldDate {
              id
              name
              dataType
            }
            ... on IssueFieldNumber {
              id
              name
              dataType
            }
          }
        }
      }
      issueReactionUpdated {
        reactionGroups {
          content
          viewerHasReacted
          reactors(first: 10) {
            totalCount
            nodes {
              __typename
              ... on Actor {
                login
                name
                avatarUrl(size: 64)
                ... on Bot {
                  isCopilot
                  isAgent
                  displayName
                }
              }
            }
          }
        }
      }
      issueFieldsUpdated {
        id
        issueFieldValues(first: 25) {
          nodes {
            ... on IssueFieldTextValue {
              field {
                ... on IssueFieldText {
                  id
                  name
                  dataType
                  visibility
                }
              }
              value
            }
            ... on IssueFieldSingleSelectValue {
              field {
                ... on IssueFieldSingleSelect {
                  id
                  name
                  dataType
                  visibility
                }
              }
              optionId
              name
              color
              description
            }
            ... on IssueFieldMultiSelectValue {
              field {
                ... on IssueFieldMultiSelect {
                  id
                  name
                  dataType
                  visibility
                }
              }
              options {
                id
                name
                color
                description
              }
              multiSelectValue: value
            }
            ... on IssueFieldDateValue {
              field {
                ... on IssueFieldDate {
                  id
                  name
                  dataType
                  visibility
                }
              }
              value
            }
            ... on IssueFieldNumberValue {
              field {
                ... on IssueFieldNumber {
                  id
                  name
                  dataType
                  visibility
                }
              }
              numberValue: value
            }
          }
        }
        viewerCanSetFields
        issueType {
          id
          name
        }
      }
      commentReactionUpdated {
        reactionGroups {
          content
          viewerHasReacted
          reactors(first: 10) {
            totalCount
            nodes {
              __typename
              ... on Actor {
                login
                name
                avatarUrl(size: 64)
                ... on Bot {
                  isCopilot
                  isAgent
                  displayName
                }
              }
            }
          }
        }
      }
      commentUpdated {
        id
        body
        bodyHTML(unfurlReferences: true)
        bodyVersion
        # Inlined from the former IssueCommentEditorBodyFragment when IssueCommentEditorBodyFragment migrated off Relay.
        databaseId
        url
        createdAt
        authorAssociation
        viewerCanUpdate
        viewerCanDelete
        viewerCanMinimize
        viewerCanUnminimize
        viewerCanReport
        viewerCanReportToMaintainer
        viewerCanBlockFromOrg
        viewerCanUnblockFromOrg
        viewerCanBlockPersonally
        viewerCanUnblockPersonally
        viewerCanPin
        viewerCanUnpin
        viaApp {
          name
          url
        }
        isHidden: isMinimized
        minimizedReason
        pendingMinimizeReason
        pendingBlock
        pendingUnblock
        showSpammyBadge
        createdViaEmail
        viewerDidAuthor
        authorToRepoOwnerSponsorship {
          createdAt
          isActive
        }
        viewerCanReadUserContentEdits
        lastEditedAt
        lastUserContentEdit {
          editor {
            url
            login
          }
        }
        pinnedBy {
          login
        }
        author {
          id
          login
          avatarUrl
          ... on User {
            name
          }
          ... on EnterpriseUserAccount {
            name
          }
          ... on Mannequin {
            name
          }
        }
        repository {
          id
          name
          owner {
            id
            login
            url
          }
          isPrivate
        }
        issue {
          id
          number
          databaseId
          author {
            login
          }
          pinnedIssueComment {
            pinnedBy {
              login
            }
            issueComment {
              id
              shortBodyHTML(limit: 700, stripBlockElements: false)
              createdAt
              url
              viewerCanUnpin
              author {
                login
                avatarUrl
                profileUrl
              }
            }
          }
        }
      }
      pinnedCommentUpdated {
        # Inlined from the former IssueCommentHeader fragment when IssueCommentHeader migrated off Relay.
        id
        databaseId
        url
        createdAt
        body
        authorAssociation
        viewerCanUpdate
        viewerCanDelete
        viewerCanMinimize
        viewerCanUnminimize
        viewerCanReport
        viewerCanReportToMaintainer
        viewerCanBlockFromOrg
        viewerCanUnblockFromOrg
        viewerCanBlockPersonally
        viewerCanUnblockPersonally
        viewerCanPin
        viewerCanUnpin
        viaApp {
          name
          url
        }
        isHidden: isMinimized
        minimizedReason
        pendingMinimizeReason
        pendingBlock
        pendingUnblock
        showSpammyBadge
        createdViaEmail
        viewerDidAuthor
        authorToRepoOwnerSponsorship {
          createdAt
          isActive
        }
        author {
          id
          login
          ... on User {
            name
          }
          ... on EnterpriseUserAccount {
            name
          }
          ... on Mannequin {
            name
          }
        }
        repository {
          id
          name
          owner {
            id
            login
            url
          }
          isPrivate
        }
        viewerCanReadUserContentEdits
        lastEditedAt
        lastUserContentEdit {
          editor {
            url
            login
          }
        }
        pinnedBy {
          login
        }
        issue {
          id
          number
          pinnedIssueComment {
            pinnedBy {
              login
            }
            issueComment {
              id
              shortBodyHTML(limit: 700, stripBlockElements: false)
              createdAt
              url
              viewerCanUnpin
              author {
                login
                avatarUrl
                profileUrl
              }
            }
          }
        }
      }
      subIssuesUpdated {
        id
        repository {
          name
          owner {
            login
          }
        }
        subIssuesConnection: subIssues {
          totalCount
        }
        subIssues(first: 100) {
          nodes {
            id
            closed
            state
            stateReason
            issueDependenciesSummary {
              blockedBy
            }
            assignees(first: 10) {
              totalCount
              edges {
                node {
                  id
                  login
                  avatarUrl
                }
              }
            }
            url
            repository {
              name
              owner {
                login
              }
            }
            databaseId
            number
            title
            titleHTML
            issueType {
              id
              name
              color
            }
            subIssuesSummary {
              total
              completed
            }
            closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
              totalCount
            }
          }
        }
        databaseId
        number
        repository {
          nameWithOwner
          visibility
          viewerCanPush
          owner {
            login
          }
        }
        parent {
          id
        }
        subIssuesSummary {
          total
        }
      }
      issueTransferStateUpdated {
        id
        repository {
          name
          owner {
            login
          }
        }
        subIssuesConnection: subIssues {
          totalCount
        }
        subIssues(first: 100) {
          nodes {
            id
            closed
            state
            stateReason
            issueDependenciesSummary {
              blockedBy
            }
            assignees(first: 10) {
              totalCount
              edges {
                node {
                  id
                  login
                  avatarUrl
                }
              }
            }
            url
            repository {
              name
              owner {
                login
              }
            }
            databaseId
            number
            title
            titleHTML
            issueType {
              id
              name
              color
            }
            subIssuesSummary {
              total
              completed
            }
            closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
              totalCount
            }
          }
        }
        databaseId
        number
        viewerCanUpdateMetadata
        repository {
          nameWithOwner
          isArchived
          visibility
          viewerCanPush
          owner {
            login
          }
        }
        parent {
          id
        }
        subIssuesSummary {
          total
        }
      }
      # Closing a sub-issue emits this instead of subIssuesUpdated. Consumers derive the completion
      # pill from the sub-issues list, which only holds when the list is fresh - hence this event.
      subIssuesSummaryUpdated {
        subIssuesSummary {
          total
          completed
        }
      }
      parentIssueUpdated {
        # Inlined from the former RelationshipsSectionFragment to keep the Relay store fresh for RelationshipsSectionInternal.
        id
        databaseId
        viewerCanUpdateMetadata
        repository {
          nameWithOwner
          isArchived
          owner {
            login
          }
        }
        topBlockedBy: blockedBy(first: 3, ranked: true) {
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        topBlocking: blocking(first: 3, ranked: true) {
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        topRelatesTo: relatesTo(first: 3, ranked: true) {
          totalCount
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        securityAlerts(first: 100) {
          nodes {
            number
            repository {
              nameWithOwner
            }
            status
            title
          }
        }
        issueDependenciesSummary {
          blockedBy
          blocking
        }
        parent {
          id
          number
          repository {
            name
            nameWithOwner
            owner {
              login
            }
          }
          state
          stateReason
          title
          titleHTML
          url
          subIssuesSummary {
            total
            completed
          }
        }
      }
      issueDependenciesSummaryUpdated {
        # Inlined from the former RelationshipsSectionFragment to keep the Relay store fresh for RelationshipsSectionInternal.
        id
        databaseId
        viewerCanUpdateMetadata
        repository {
          nameWithOwner
          isArchived
          owner {
            login
          }
        }
        parent {
          id
          titleHTML
          url
          number
          repository {
            nameWithOwner
          }
          state
          stateReason
          subIssuesSummary {
            total
            completed
          }
        }
        topBlockedBy: blockedBy(first: 3, ranked: true) {
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        topBlocking: blocking(first: 3, ranked: true) {
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        topRelatesTo: relatesTo(first: 3, ranked: true) {
          totalCount
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        securityAlerts(first: 100) {
          nodes {
            number
            repository {
              nameWithOwner
            }
            status
            title
          }
        }
        state
        issueDependenciesSummary {
          blockedBy
          blocking
        }
        blockedBy(first: 1, ranked: true) {
          nodes {
            titleHTML
            number
            url
            repository {
              name
              owner {
                login
              }
            }
          }
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const timelineSubscription = graphql`
  subscription IssueViewerSubscriptionTimelineSubscription($issueId: ID!, $connections: [ID!]!, $skip: Int) {
    issueUpdated(id: $issueId) {
      # Which timeline the appended row belongs to: the invalidation below is scoped to one issue.
      issue {
        number
        repository {
          name
          owner {
            login
          }
        }
      }
      issueTimelineUpdated {
        timelineItems(skip: $skip, first: 10, visibleEventsOnly: true) {
          totalCount
          edges @appendEdge(connections: $connections) {
            node {
              __id
              __typename
              ... on SubscribedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on UnsubscribedEvent {
                createdAt
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on MentionedEvent {
                createdAt
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on IssueComment {
                id
                databaseId
                url
                createdAt
                authorAssociation
                viewerCanDelete
                viewerCanMinimize
                viewerCanUnminimize
                viewerCanReport
                viewerCanReportToMaintainer
                viewerCanBlockFromOrg
                viewerCanUnblockFromOrg
                viewerCanBlockPersonally
                viewerCanUnblockPersonally
                viewerCanPin
                viewerCanUnpin
                viaApp {
                  name
                  url
                }
                minimizedReason
                pendingMinimizeReason
                pendingBlock
                pendingUnblock
                showSpammyBadge
                createdViaEmail
                viewerDidAuthor
                authorToRepoOwnerSponsorship {
                  createdAt
                  isActive
                }
                viewerCanReadUserContentEdits
                lastEditedAt
                lastUserContentEdit {
                  editor {
                    url
                    login
                  }
                }
                pinnedBy {
                  login
                }
                author {
                  id
                  avatarUrl
                  login
                  __typename
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                issue {
                  id
                  number
                  locked
                  databaseId
                  author {
                    login
                  }
                }
                body
                bodyHTML(unfurlReferences: true)
                bodyVersion
                isHidden: isMinimized
                viewerCanUpdate
                repository {
                  id
                  name
                  owner {
                    id
                    login
                    url
                  }
                  isPrivate
                  viewerCanCreateIssues
                  slashCommandsEnabled
                  nameWithOwner
                  databaseId
                }
                reactionGroups {
                  content
                  viewerHasReacted
                  reactors(first: 10) {
                    totalCount
                    nodes {
                      __typename
                      ... on Actor {
                        login
                        name
                        avatarUrl(size: 64)
                        ... on Bot {
                          isCopilot
                          isAgent
                          displayName
                        }
                      }
                    }
                  }
                }
              }
              ... on Reactable {
                reactionGroups {
                  content
                  viewerHasReacted
                  reactors(first: 10) {
                    totalCount
                    nodes {
                      __typename
                      ... on Actor {
                        login
                        name
                        avatarUrl(size: 64)
                        ... on Bot {
                          isCopilot
                          isAgent
                          displayName
                        }
                      }
                    }
                  }
                }
              }
              ... on ClosedEvent {
                databaseId
                createdAt
                stateReason
                duplicateOf {
                  __typename
                  ... on Issue {
                    id
                    titleHTML
                    url
                    number
                    stateReason
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                  ... on PullRequest {
                    id
                    pullTitleHTML: titleHTML
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                }
                closingProjectItemStatus
                closer {
                  __typename
                  ... on ProjectV2 {
                    url
                    title
                  }
                  ... on PullRequest {
                    url
                    number
                    repository {
                      id
                      name
                      owner {
                        login
                      }
                    }
                  }
                  ... on Commit {
                    url
                    abbreviatedOid
                    repository {
                      name
                      owner {
                        login
                      }
                    }
                  }
                }
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                intent {
                  rationale
                }
              }
              ... on ReopenedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on LockedEvent {
                databaseId
                createdAt
                lockReason
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on UnlockedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on PinnedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on UnpinnedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on LabeledEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                label {
                  nameHTML
                  name
                  color
                  id
                  description
                  archivedAt
                }
                intent {
                  rationale
                }
              }
              ... on RenamedTitleEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                currentTitle
                previousTitle
              }
              ... on UnlabeledEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                label {
                  nameHTML
                  name
                  color
                  id
                  description
                  archivedAt
                }
              }
              ... on UnassignedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                assignee {
                  ... on Actor {
                    login
                  }
                  ... on User {
                    __typename
                    login
                    resourcePath
                    name
                  }
                  ... on Mannequin {
                    __typename
                    login
                    resourcePath
                    name
                  }
                  ... on Organization {
                    __typename
                    login
                    resourcePath
                  }
                  ... on Bot {
                    __typename
                    login
                    resourcePath
                    isCopilot
                    displayName
                    isAgent
                  }
                }
              }
              ... on AssignedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                assignee {
                  ... on Actor {
                    login
                  }
                  ... on User {
                    __typename
                    login
                    resourcePath
                    name
                  }
                  ... on Mannequin {
                    __typename
                    login
                    resourcePath
                    name
                  }
                  ... on Organization {
                    __typename
                    login
                    resourcePath
                  }
                  ... on Bot {
                    __typename
                    login
                    resourcePath
                    isCopilot
                    displayName
                    isAgent
                  }
                }
                intent {
                  rationale
                }
              }
              ... on CommentDeletedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                deletedCommentAuthor {
                  __typename
                  login
                  ... on User {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                }
              }
              ... on UserBlockedEvent {
                databaseId
                createdAt
                blockDuration
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                blockedUser: subject {
                  login
                  name
                }
              }
              ... on CrossReferencedEvent {
                referencedAt
                willCloseTarget
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                innerSource: source {
                  __typename
                  ... on Issue {
                    id
                    titleHTML
                    url
                    number
                    stateReason
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                  ... on PullRequest {
                    id
                    pullTitleHTML: titleHTML
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                }
              }
              ... on MilestonedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                milestoneTitle
                milestone {
                  url
                }
              }
              ... on DemilestonedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                milestoneTitle
                milestone {
                  url
                }
              }
              ... on ReferencedEvent {
                id
                databaseId
                willCloseSubject
                subject {
                  __typename
                }
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                commit {
                  message
                  messageHeadlineHTML
                  messageBodyHTML
                  url
                  abbreviatedOid
                  repository {
                    name
                    owner {
                      login
                    }
                    defaultBranch
                  }
                }
                createdAt
              }
              ... on ConnectedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                subject {
                  __typename
                  ... on PullRequest {
                    title
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      name
                      owner {
                        login
                      }
                    }
                  }
                }
              }
              ... on TransferredEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                fromRepository {
                  nameWithOwner
                  url
                }
              }
              ... on DisconnectedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                subject {
                  __typename
                  ... on PullRequest {
                    title
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      name
                      owner {
                        login
                      }
                    }
                  }
                }
              }
              ... on MarkedAsDuplicateEvent {
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                duplicate {
                  ... on Issue {
                    __typename
                    id
                    titleHTML
                    url
                    number
                    stateReason
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                  ... on PullRequest {
                    __typename
                    id
                    pullTitleHTML: titleHTML
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                }
                canonical {
                  ... on Issue {
                    __typename
                    id
                    titleHTML
                    url
                    number
                    stateReason
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                  ... on PullRequest {
                    __typename
                    id
                    pullTitleHTML: titleHTML
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                }
                isCanonicalOfClosedDuplicate
                databaseId
                viewerCanUndo
              }
              ... on UnmarkedAsDuplicateEvent {
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                canonical {
                  ... on Issue {
                    __typename
                    id
                    titleHTML
                    url
                    number
                    stateReason
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                  ... on PullRequest {
                    __typename
                    id
                    pullTitleHTML: titleHTML
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                }
                isCanonicalOfClosedDuplicate
                databaseId
              }
              ... on ConvertedToDiscussionEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                discussion {
                  url
                  number
                }
              }
              ... on AddedToProjectV2Event {
                databaseId
                createdAt
                actor {
                  login
                  avatarUrl(size: 64)
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                project {
                  title
                  url
                }
              }
              ... on RemovedFromProjectV2Event {
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                project {
                  title
                  url
                }
              }
              ... on ProjectV2ItemStatusChangedEvent {
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                project {
                  title
                  url
                }
                previousStatus
                status
              }
              ... on ConvertedFromDraftEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on SubIssueAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                subIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                  databaseId
                }
              }
              ... on SubIssueRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                subIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                  databaseId
                }
              }
              ... on ParentIssueAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                parent {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                  databaseId
                }
              }
              ... on ParentIssueRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                parent {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                  databaseId
                }
              }
              ... on BlockedByAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                blockingIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                }
              }
              ... on BlockedByRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                blockingIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                }
              }
              ... on BlockingAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                blockedIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                }
              }
              ... on BlockingRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                blockedIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                }
              }
              ... on RelatesToAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                relatedIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                }
              }
              ... on RelatesToRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                relatedIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                }
              }
              ... on IssueTypeAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueType {
                  name
                  color
                }
                intent {
                  rationale
                }
              }
              ... on IssueTypeRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueType {
                  name
                  color
                }
              }
              ... on IssueTypeChangedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueType {
                  name
                  color
                }
                prevIssueType {
                  name
                  color
                }
                intent {
                  rationale
                }
              }
              ... on IssueFieldChangedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueField {
                  ... on IssueFieldText {
                    dataType
                    name
                  }
                  ... on IssueFieldDate {
                    name
                    dataType
                  }
                  ... on IssueFieldNumber {
                    name
                    dataType
                  }
                  ... on IssueFieldSingleSelect {
                    name
                    dataType
                  }
                  ... on IssueFieldMultiSelect {
                    name
                    dataType
                  }
                }
                newColor
                newOptions {
                  name
                  color
                }
                newValue
                intent {
                  rationale
                }
              }
              ... on IssueFieldRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueField {
                  ... on IssueFieldText {
                    dataType
                    name
                  }
                  ... on IssueFieldDate {
                    name
                    dataType
                  }
                  ... on IssueFieldNumber {
                    name
                    dataType
                  }
                  ... on IssueFieldSingleSelect {
                    name
                    dataType
                  }
                  ... on IssueFieldMultiSelect {
                    name
                    dataType
                  }
                }
                intent {
                  rationale
                }
              }
              ... on IssueFieldAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueField {
                  ... on IssueFieldText {
                    dataType
                    name
                  }
                  ... on IssueFieldDate {
                    name
                    dataType
                  }
                  ... on IssueFieldNumber {
                    name
                    dataType
                  }
                  ... on IssueFieldSingleSelect {
                    name
                    dataType
                  }
                  ... on IssueFieldMultiSelect {
                    name
                    dataType
                  }
                }
                options {
                  name
                  color
                }
                value
                color
                intent {
                  rationale
                }
              }
              ... on IssueCommentPinnedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                issueComment {
                  url
                  databaseId
                }
              }
              ... on IssueCommentUnpinnedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                issueComment {
                  url
                  databaseId
                }
              }
            }
          }
        }
      }
    }
  }
`

export const useIssueViewerSubscription = (issueId: string, initialSkip: number | null, connectionId: string) => {
  const relayEnvironment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const tanstackSecondaryViewEnabled = isFeatureEnabled('issue_viewer_secondary_view_tanstack')
  const timelineTanstackEnabled = isFeatureEnabled('issue_viewer_paved_path')
  const [skipCount, setSkipCount] = useState(initialSkip || 0)

  const metadataConfig = useMemo(
    () => ({
      subscription: metadataSubscription,
      variables: {issueId},
      onNext: (resp: IssueViewerSubscriptionMetadataSubscription$data | null | undefined) => {
        if (!resp?.issueUpdated?.issue) return

        // When a comment is deleted, the mutation already handles edge removal
        // and totalCount via its updater. A force refresh here would race with
        // the in-flight mutation even if other metadata fields are also set.
        if (resp.issueUpdated.deletedCommentId) return

        // Only force-refresh when the subscription carries an actual metadata
        // change. Timeline-only events (comment creation, etc.) arrive with no
        // metadata fields set and are already handled by the timeline
        // subscription via @appendEdge. A force refresh in those cases
        // re-fetches with backTimelineItems(last: 0), resetting the Relay back
        // connection and racing with the in-flight @appendEdge — causing
        // comments to disappear or reorder.
        // See: https://github.com/github/issues/issues/19785
        const {issue, deletedCommentId, ...metadataFields} = resp.issueUpdated
        const hasMetadataChanges = Object.values(metadataFields).some(v => v != null)

        if (!hasMetadataChanges) return

        const number = resp.issueUpdated.issue.number
        const repoName = resp.issueUpdated.issue.repository.name
        const repoOwner = resp.issueUpdated.issue.repository.owner.login

        // Both render from TanStack queries this Relay refresh never reaches. Either flag being on
        // is enough: invalidating a key nothing holds is a no-op.
        if (tanstackSecondaryViewEnabled || isFeatureEnabled('issue_viewer_paved_path')) {
          invalidateForIssueUpdate({
            queryClient,
            payload: metadataFields,
            issueId,
            coordinates: {owner: repoOwner, repo: repoName, number},
          })
        }

        cacheForceRefresh(relayEnvironment, {
          query: IssueViewerViewGraphqlQuery,
          number,
          owner: repoOwner,
          repo: repoName,
        })
      },
    }),
    [issueId, relayEnvironment, queryClient, tanstackSecondaryViewEnabled],
  )

  const timelineConfig = useMemo(
    () => ({
      subscription: timelineSubscription,
      onNext: (resp: IssueViewerSubscriptionTimelineSubscription$data | null | undefined) => {
        if (!resp?.issueUpdated?.issueTimelineUpdated) return

        // Fires once the timeline row exists; the metadata subscription fires up to ~3s earlier,
        // when the mutation returns, so refetching there reads a timeline without it. That is why
        // `keysForScope` leaves appended rows to this handler and covers only edits.
        // Since we only care about events after a certain point, we need to resubscribe with the new skip count if there are newer events
        const newTotalCount = resp.issueUpdated?.issueTimelineUpdated?.timelineItems?.totalCount

        const issue = resp.issueUpdated.issue
        if (timelineTanstackEnabled && issue) {
          // Through `invalidateScopes` rather than invalidating directly, for the coalescing and
          // the in-flight-write guard the edit path already gets -- an optimistic row is still
          // settling when its own event arrives.
          invalidateScopes({
            queryClient,
            scopes: ['timeline'],
            issueId,
            coordinates: {owner: issue.repository.owner.login, repo: issue.repository.name, number: issue.number},
          })
        }

        if (newTotalCount != null && newTotalCount > skipCount) {
          setSkipCount(newTotalCount)
        }
      },
      variables: {
        issueId,
        connections: [connectionId],
        skip: skipCount,
      },
    }),
    [issueId, skipCount, connectionId, timelineTanstackEnabled, queryClient],
  )

  useSubscription<IssueViewerSubscriptionMetadataSubscription>(metadataConfig)
  useSubscription<IssueViewerSubscriptionTimelineSubscription>(timelineConfig)
}
